import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { FounderTicket, FounderTicketStatus, ContentPreviewMeta } from '../../services/founder-board-api.js';
import { listTicketsByCompanyAndType, parseContentPreviewMeta, approveContentPreview, respondToTicket, driveUrlToThumbnail } from '../../services/founder-board-api.js';
import { wsClient } from '../../services/websocket-client.js';

type ContentFilter = 'pending' | 'approved' | 'all';

@customElement('cp-company-content-preview')
export class CpCompanyContentPreview extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;

  @state() private _tickets: FounderTicket[] = [];
  @state() private _loading = false;
  @state() private _selectedTicket: FounderTicket | null = null;
  @state() private _selectedMeta: ContentPreviewMeta | null = null;
  @state() private _editedCaption = '';
  @state() private _showCaptionEdit = false;
  @state() private _showRejectForm = false;
  @state() private _rejectComment = '';
  @state() private _submitting = false;
  @state() private _error = '';
  @state() private _filter: ContentFilter = 'pending';

  private _wsHandler: ((e: Event) => void) | null = null;

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._selectedTicket) this._closePanel();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
    document.addEventListener('keydown', this._onKeyDown);

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string }>).detail;
      if (
        msg.type === 'founder-ticket:created' ||
        msg.type === 'founder-ticket:updated' ||
        msg.type === 'founder-ticket:commented' ||
        msg.type === 'founder-ticket:deleted'
      ) {
        void this._load();
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId') && this.companyId > 0) {
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (this.companyId <= 0) return;
    this._loading = true;
    try {
      this._tickets = await listTicketsByCompanyAndType(this.companyId, 'content_preview');
    } catch {
      // silent
    } finally {
      this._loading = false;
    }
  }

  private get _filteredTickets(): FounderTicket[] {
    if (this._filter === 'all') return this._tickets;
    if (this._filter === 'pending') return this._tickets.filter(t => t.status === 'open' || t.status === 'waiting');
    return this._tickets.filter(t => t.status === 'approved');
  }

  private _openPanel(ticket: FounderTicket): void {
    this._selectedTicket = ticket;
    this._selectedMeta = parseContentPreviewMeta(ticket.description);
    this._editedCaption = this._selectedMeta?.caption ?? '';
    this._showCaptionEdit = false;
    this._showRejectForm = false;
    this._rejectComment = '';
    this._error = '';
  }

  private _closePanel(): void {
    this._selectedTicket = null;
    this._selectedMeta = null;
    this._showCaptionEdit = false;
    this._showRejectForm = false;
    this._rejectComment = '';
    this._error = '';
  }

  private _onOverlayClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('cp-content-panel__overlay')) {
      this._closePanel();
    }
  }

  private async _handleApprove(): Promise<void> {
    if (!this._selectedTicket) return;
    this._submitting = true;
    this._error = '';
    try {
      const editedCaption = this._showCaptionEdit && this._editedCaption !== this._selectedMeta?.caption
        ? this._editedCaption
        : undefined;
      await approveContentPreview(this._selectedTicket.id, editedCaption);
      this._closePanel();
      void this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Genehmigen';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleReject(): Promise<void> {
    if (!this._selectedTicket || !this._rejectComment.trim()) return;
    this._submitting = true;
    this._error = '';
    try {
      await respondToTicket(this._selectedTicket.id, this._rejectComment.trim(), 'done');
      this._closePanel();
      void this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Ablehnen';
    } finally {
      this._submitting = false;
    }
  }

  private _getPostTypeIcon(postType: string): string {
    switch (postType) {
      case 'carousel': return '\u{1F3A0}';
      case 'reel': return '\u{1F3AC}';
      default: return '\u{1F4F7}';
    }
  }

  private _getStatusClass(status: FounderTicketStatus): string {
    if (status === 'open' || status === 'waiting') return 'pending';
    if (status === 'approved') return 'approved';
    return 'done';
  }

  private _getStatusLabel(status: FounderTicketStatus): string {
    if (status === 'open' || status === 'waiting') return 'Ausstehend';
    if (status === 'approved') return 'Genehmigt';
    return 'Erledigt';
  }

  private _renderFilterTabs() {
    const counts = {
      pending: this._tickets.filter(t => t.status === 'open' || t.status === 'waiting').length,
      approved: this._tickets.filter(t => t.status === 'approved').length,
      all: this._tickets.length,
    };

    const tab = (filter: ContentFilter, label: string) => html`
      <button
        class="cp-content-filters__tab ${this._filter === filter ? 'cp-content-filters__tab--active' : ''}"
        @click=${() => { this._filter = filter; }}
      >${label}${counts[filter] > 0 ? ` (${counts[filter]})` : ''}</button>
    `;

    return html`
      <div class="cp-content-filters">
        ${tab('pending', 'Ausstehend')}
        ${tab('approved', 'Genehmigt')}
        ${tab('all', 'Alle')}
      </div>
    `;
  }

  private _renderCard(ticket: FounderTicket) {
    const meta = parseContentPreviewMeta(ticket.description);
    if (!meta) return nothing;

    const firstImage = meta.image_urls[0];
    const statusClass = this._getStatusClass(ticket.status);

    return html`
      <div class="cp-content-card" @click=${() => this._openPanel(ticket)}>
        <div class="cp-content-card__image">
          ${firstImage ? html`
            <img src="${driveUrlToThumbnail(firstImage)}" alt="Post Preview" loading="lazy" />
          ` : html`
            <div class="cp-content-card__image-placeholder">${this._getPostTypeIcon(meta.post_type)}</div>
          `}
          <span class="cp-content-card__status cp-content-card__status--${statusClass}">
            ${this._getStatusLabel(ticket.status)}
          </span>
        </div>
        <div class="cp-content-card__body">
          <p class="cp-content-card__caption">${meta.caption}</p>
          <div class="cp-content-card__meta">
            ${meta.posting_time ? html`<span>${meta.posting_time}</span>` : nothing}
            <span class="cp-content-card__meta-icon">${this._getPostTypeIcon(meta.post_type)}</span>
            <span>${meta.post_type === 'carousel' ? 'Carousel' : meta.post_type === 'reel' ? 'Reel' : 'Post'}</span>
            ${meta.image_urls.length > 1 ? html`<span>${meta.image_urls.length} Bilder</span>` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private _renderPanel() {
    const ticket = this._selectedTicket;
    const meta = this._selectedMeta;
    if (!ticket || !meta) return nothing;

    const isPending = ticket.status === 'open' || ticket.status === 'waiting';
    const isSingleImage = meta.image_urls.length <= 1;

    return html`
      <div class="cp-content-panel__overlay" @click=${(e: MouseEvent) => this._onOverlayClick(e)}>
        <div class="cp-content-panel">
          <div class="cp-content-panel__header">
            <h2 class="cp-content-panel__title">Instagram Post Vorschau</h2>
            <button class="cp-content-panel__close" @click=${() => this._closePanel()}>&times;</button>
          </div>

          <div class="cp-content-panel__body">
            <!-- Image gallery -->
            <div class="cp-content-panel__gallery ${isSingleImage ? 'cp-content-panel__gallery--single' : ''}">
              ${meta.image_urls.map(url => html`
                <img src="${driveUrlToThumbnail(url)}" alt="Post-Bild" loading="lazy" />
              `)}
              ${meta.image_urls.length === 0 ? html`
                <div class="cp-content-card__image-placeholder" style="width:100%;aspect-ratio:4/5;">
                  ${this._getPostTypeIcon(meta.post_type)}
                </div>
              ` : nothing}
            </div>

            <!-- Caption -->
            <div class="cp-content-panel__section">
              <h3 class="cp-content-panel__label">Caption</h3>
              <p class="cp-content-panel__caption-text">${meta.caption}</p>
            </div>

            <!-- Hashtags -->
            ${meta.hashtags.length > 0 ? html`
              <div class="cp-content-panel__section">
                <h3 class="cp-content-panel__label">Hashtags</h3>
                <div class="cp-content-panel__hashtags">
                  ${meta.hashtags.map(tag => html`
                    <span class="cp-content-hashtag">${tag.startsWith('#') ? tag : `#${tag}`}</span>
                  `)}
                </div>
              </div>
            ` : nothing}

            <!-- Meta -->
            <div class="cp-content-panel__meta-row">
              ${meta.posting_time ? html`<span>Posting: ${meta.posting_time}</span>` : nothing}
              <span>Typ: ${meta.post_type === 'carousel' ? 'Carousel' : meta.post_type === 'reel' ? 'Reel' : 'Post'}</span>
            </div>

            <!-- Founder comment (if already responded) -->
            ${ticket.founder_comment ? html`
              <div class="cp-content-panel__section">
                <h3 class="cp-content-panel__label">Antwort</h3>
                <p class="cp-content-panel__caption-text" style="background:var(--color-bg-tertiary);padding:var(--spacing-sm) var(--spacing-md);border-radius:var(--radius-md);">
                  ${ticket.founder_comment}
                </p>
              </div>
            ` : nothing}
          </div>

          <div class="cp-content-panel__footer">
            ${this._error ? html`<p class="cp-content-panel__error">${this._error}</p>` : nothing}

            ${isPending && !this._showCaptionEdit && !this._showRejectForm ? html`
              <div class="cp-content-panel__actions">
                <button class="cp-content-panel__btn cp-content-panel__btn--approve"
                  ?disabled=${this._submitting}
                  @click=${() => this._handleApprove()}>
                  Genehmigen & Posten
                </button>
                <button class="cp-content-panel__btn cp-content-panel__btn--edit"
                  @click=${() => { this._showCaptionEdit = true; }}>
                  Caption anpassen
                </button>
                <button class="cp-content-panel__btn cp-content-panel__btn--reject"
                  @click=${() => { this._showRejectForm = true; }}>
                  Ablehnen
                </button>
              </div>
            ` : nothing}

            ${this._showCaptionEdit ? html`
              <div class="cp-content-panel__reply-form">
                <h3 class="cp-content-panel__label">Caption bearbeiten</h3>
                <textarea
                  class="cp-content-caption-edit"
                  rows="4"
                  .value=${this._editedCaption}
                  @input=${(e: Event) => { this._editedCaption = (e.target as HTMLTextAreaElement).value; }}
                ></textarea>
                <div class="cp-content-panel__actions">
                  <button class="cp-content-panel__btn cp-content-panel__btn--approve"
                    ?disabled=${this._submitting || !this._editedCaption.trim()}
                    @click=${() => this._handleApprove()}>
                    Mit neuer Caption genehmigen
                  </button>
                  <button class="cp-content-panel__btn cp-content-panel__btn--reject"
                    @click=${() => { this._showCaptionEdit = false; }}>
                    Abbrechen
                  </button>
                </div>
              </div>
            ` : nothing}

            ${this._showRejectForm ? html`
              <div class="cp-content-panel__reply-form">
                <textarea
                  class="cp-content-panel__textarea"
                  rows="3"
                  placeholder="Ablehnungsgrund..."
                  .value=${this._rejectComment}
                  @input=${(e: Event) => { this._rejectComment = (e.target as HTMLTextAreaElement).value; }}
                ></textarea>
                <div class="cp-content-panel__actions">
                  <button class="cp-content-panel__btn cp-content-panel__btn--approve"
                    style="background:rgba(239,68,68,0.9)"
                    ?disabled=${this._submitting || !this._rejectComment.trim()}
                    @click=${() => this._handleReject()}>
                    Ablehnen
                  </button>
                  <button class="cp-content-panel__btn cp-content-panel__btn--reject"
                    @click=${() => { this._showRejectForm = false; this._rejectComment = ''; }}>
                    Abbrechen
                  </button>
                </div>
              </div>
            ` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  override render() {
    if (this._loading && this._tickets.length === 0) {
      return html`<div class="cp-content-loading">Content wird geladen...</div>`;
    }

    const filtered = this._filteredTickets;

    return html`
      ${this._renderFilterTabs()}

      ${filtered.length === 0 ? html`
        <div class="cp-content-empty">
          ${this._filter === 'pending' ? 'Keine ausstehenden Content-Vorschauen' :
            this._filter === 'approved' ? 'Noch keine genehmigten Posts' :
            'Keine Content-Vorschauen vorhanden'}
        </div>
      ` : html`
        <div class="cp-content-grid">
          ${filtered.map(ticket => this._renderCard(ticket))}
        </div>
      `}

      ${this._selectedTicket ? this._renderPanel() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-content-preview': CpCompanyContentPreview;
  }
}
